/**
 * Composed goal-recipe catalog entries for the retail industry (plan §6.3 /
 * §6.5). Filled by the retail P4b copywriting task — owned exclusively by it.
 * Entries omit `previewPath` (added automatically in template-catalog.ts).
 *
 * `previewFixture` reuses an existing retail seed fixture (thumbnail/preview
 * refresh is a separate track per plan §6.5) — chosen for the closest visual
 * match to each goal's structure:
 *  - `sell`     -> `apparel-demo`  (grid-forward product listing)
 *  - `discovery`-> `retail-demo`   (general-purpose storefront)
 *  - `repeat`   -> `grocery-demo`  (frequent-update, seasonal-news framing)
 *  - `trust`    -> `craft-demo`    (maker/owner-story framing, closest to the
 *                                   new `people` role's "顔が見える" content)
 *
 * Copy discipline (plan §3.6 / task spec): owner vocabulary, no marketing
 * jargon, no exaggerated claims. `features`/`concerns.answer` reference only
 * real platform capabilities (かんたん更新, 公開ボタン, 業種別プロ写真, そのまま
 * 使える初期文言, 高速表示, 独自ドメイン(プランによる), 外部ECサイトへのリンク導線).
 * Retail has no owned form, cart, checkout, or inventory management — never
 * promised here.
 */
import type { TemplateEntryDraft } from '../template-catalog';

export const COMPOSED_RETAIL_TEMPLATES: readonly TemplateEntryDraft[] = [
  {
    slug: 'retail-sell',
    theme: 'retail',
    displayName: '商品をまっすぐ届けるショップサイト',
    category: '小売・ショップ',
    description:
      '商品一覧を大きく見せ、そのままオンラインショップや店舗への案内につなげる構成。「見る→買う」までの迷いを減らしたいお店に。',
    thumb: '/lp/template-retail-apparel.webp',
    previewFixture: 'apparel-demo',
    fit: '雑貨店、アパレル、食料品店、外部ECサイトと併用したいお店',
    features: [
      '商品グリッド一覧',
      '季節・新着のお知らせ枠',
      '店舗アクセス案内',
      '外部ECサイトへのリンク導線',
    ],
    goal: 'sell',
    industry: 'retail',
    subIndustries: ['総合', 'アパレル', '花屋', '食料品', 'クラフト・雑貨', 'ショールーム'],
    outcomeStory:
      '公開したその日から、ホームページの商品一覧がそのまま「今お店に並んでいるもの」を伝える棚になります。新商品を追加したくなったら、かんたん更新で写真と説明を載せるだけ。オンラインショップをお持ちなら、そちらへの案内もサイトの中に置けます。お客様は迷わず「見る」から「買う」までたどり着けます。',
    concerns: [
      {
        worry: '商品写真をまだ用意できていない',
        answer:
          '業種別のプロ写真が最初から入っているので、空っぽの状態にはなりません。撮影が済み次第、差し替えるだけです。',
      },
      {
        worry: '商品説明の文章を書くのが苦手',
        answer: 'そのまま使える初期文言が入っています。書き直したい部分だけ直せば十分です。',
      },
      {
        worry: '更新が続けられるか不安',
        answer:
          '商品の追加やお知らせの差し替えは、スマホから1分でできるかんたん更新でまかなえます。',
      },
      {
        worry: 'カートや決済の仕組みまで作れるか心配',
        answer:
          'このテンプレートはカートや決済は持たず、お持ちの外部ECサイトへの案内に徹します。難しい設定は不要です。',
      },
    ],
    businessFlow: [
      '朝、新商品が入荷したらスマホで写真と商品名を追加する',
      '季節のお知らせやセール情報を1分で差し替える',
      '「サイトを公開」ボタンを押すと、その場で反映される',
      'オンラインショップをお持ちなら、案内リンクの行き先だけ確認しておく',
    ],
  },
  {
    slug: 'retail-discovery',
    theme: 'retail',
    displayName: '通りすがりのお客様に見つけてもらうショップサイト',
    category: '小売・ショップ',
    description:
      '営業時間・場所・お店の雰囲気をファーストビュー直下に置く構成。検索や地図で見つけた人に「今日行けるお店かどうか」をすぐ伝えたいお店に。',
    thumb: '/lp/template-retail-flower.webp',
    previewFixture: 'retail-demo',
    fit: '雑貨店、花屋、食料品店、通りがかりのお客様を大切にしたいお店',
    features: [
      '営業時間・アクセスの前面表示',
      '高速表示',
      '独自ドメイン対応（プランによる）',
      'お店の特徴紹介',
    ],
    goal: 'discovery',
    industry: 'retail',
    subIndustries: ['総合', 'アパレル', '花屋', '食料品', 'クラフト・雑貨', 'ショールーム'],
    outcomeStory:
      'スマホで「近くの○○屋」と検索してたどり着いた人が、開いてすぐに営業時間と場所を確認できます。表示も速いので、立ち止まって読み込みを待たせることがありません。お店の特徴を紹介する棚も用意されているので、「どんなお店か」が一目で伝わり、そのまま来店につながります。',
    concerns: [
      {
        worry: 'ホームページを持っていなかったので、そもそも探してもらえるか不安',
        answer:
          '営業時間・場所・お店の特徴を前面に出す構成なので、検索や地図から来た方にすぐ伝わります。',
      },
      {
        worry: '表示が遅いと途中で閉じられてしまいそう',
        answer: '高速表示を意識した作りなので、スマホでもすぐに開けます。',
      },
      {
        worry: '文章を書く時間がない',
        answer:
          'そのまま使える初期文言が入っているので、営業時間や住所など数字部分を直すだけで公開できます。',
      },
    ],
    businessFlow: [
      '営業時間や定休日が変わったら、その都度サイトを直す',
      'お店の特徴やお知らせを1分のかんたん更新で差し替える',
      '「サイトを公開」ボタンを押して、変更をすぐ反映する',
      '独自ドメインをお持ちなら（プランによる）、お店の住所とあわせて案内する',
    ],
  },
  {
    slug: 'retail-repeat',
    theme: 'retail',
    displayName: '常連さんとつながり続けるショップサイト',
    category: '小売・ショップ',
    description:
      'お知らせを目立つ位置に置き、更新を前提にした構成。新商品や季節の入荷情報をこまめに届けて、また来たくなる理由を作りたいお店に。',
    thumb: '/lp/template-retail-grocery.webp',
    previewFixture: 'grocery-demo',
    fit: '食料品店、花屋、クラフト・雑貨店、更新をこまめに続けたいお店',
    features: [
      'お知らせ枠（ヒーロー直下）',
      'かんたん更新前提の商品一覧',
      '季節・新着の案内枠',
      '外部ECサイトへのリンク導線',
    ],
    goal: 'repeat',
    industry: 'retail',
    subIndustries: ['総合', 'アパレル', '花屋', '食料品', 'クラフト・雑貨', 'ショールーム'],
    outcomeStory:
      'ホームページを開くたびに、常連のお客様が「今週は何が入っているか」を確認しに来てくれるサイトになります。新商品の入荷や季節のお知らせは、スマホから1分のかんたん更新で追加するだけ。前回の更新から時間が経っていないサイトは、お客様にとって「今も元気に営業しているお店」の証になります。',
    concerns: [
      {
        worry: '毎回更新するのが手間で続かなそう',
        answer:
          'お知らせや商品の追加は1分でできるかんたん更新なので、開店準備の合間にも入力できます。',
      },
      {
        worry: '何を書けばいいかわからない',
        answer:
          'そのまま使える初期文言が入っているので、入荷した商品名と一言を差し替えるだけで形になります。',
      },
      {
        worry: '更新をサボっていることがお客様にバレそう',
        answer:
          'お知らせはいつでも「サイトを公開」ボタンで即座に反映できるので、気づいたときにすぐ発信できます。',
      },
      {
        worry: 'オンライン販売の仕組みまで自分で作れるか不安',
        answer:
          '在庫管理や決済は持たず、お持ちの外部ECサイトへの案内に徹する作りなので、覚えることが少なく済みます。',
      },
    ],
    businessFlow: [
      '入荷や季節のお知らせを、開店前にスマホで1分追加する',
      '「サイトを公開」ボタンを押して、その場で反映する',
      '商品一覧を軽く見直して、売り切れたものを外す',
      '外部ECサイトをお持ちなら、案内リンクの行き先を定期的に確認する',
    ],
  },
  {
    slug: 'retail-trust',
    theme: 'retail',
    displayName: '店主の顔が見えるショップサイト',
    category: '小売・ショップ',
    description:
      '店主やスタッフの紹介を前面に出す構成。「誰が選んでいるか」「誰が作っているか」を伝えて、安心して来店・相談してもらいたいお店に。',
    thumb: '/lp/template-retail-craft.webp',
    previewFixture: 'craft-demo',
    fit: 'クラフト・工房、花屋、こだわりの品を扱うお店、対面での相談を大切にしたいお店',
    features: [
      '店主・スタッフ紹介',
      'お店の特徴紹介',
      '来店・外部ECサイトへの案内導線',
      'そのまま使える初期文言',
    ],
    goal: 'trust',
    industry: 'retail',
    subIndustries: ['総合', 'アパレル', '花屋', '食料品', 'クラフト・雑貨', 'ショールーム'],
    outcomeStory:
      'サイトを開いたお客様が最初に出会うのは、商品ではなく店主やスタッフの顔です。誰がどんな想いで商品を選び、作っているのかを知ってもらうことで、「このお店なら安心して相談できそう」と感じてもらえます。ページの写真や紹介文はいつでも書き直せるので、お店の今の雰囲気に合わせて育てていけます。',
    concerns: [
      {
        worry: '自分の紹介文をどう書けばいいかわからない',
        answer:
          'そのまま使える初期文言が入っているので、名前や役割、こだわりの部分だけ書き換えれば公開できます。',
      },
      {
        worry: 'プロっぽい写真がない',
        answer:
          '業種別のプロ写真が最初から入っています。ご自身の写真が用意でき次第、差し替えるだけです。',
      },
      {
        worry: 'お客様からの相談をどう受け付ければいいか不安',
        answer:
          '来店案内や外部ECサイトへの導線で対応する構成です。フォームは持たない分、設定に迷うことがありません。',
      },
    ],
    businessFlow: [
      '店主・スタッフ紹介の文章や写真を見直し、今の雰囲気に合わせて更新する',
      'お店の特徴やこだわりを1分のかんたん更新で書き足す',
      '「サイトを公開」ボタンで変更をすぐ反映する',
      '来店や外部ECサイトへの案内リンクが正しい行き先かときどき確認する',
    ],
  },
];
